import type { ReactNode } from 'react';

import { RedirectToFirstModule, RequireModuleRoute } from '@/components/auth/RequireModuleRoute';
import { useAccess } from '@/hooks/useAccess';
import { ROUTE_MODULES } from '@/lib/modules';
import { StaffNotAuthorizedPage } from '@/pages/StaffNotAuthorizedPage';

export interface RouteGuardProps {
  /** The exact top-level `<Route path>`; a key of `ROUTE_MODULES`. */
  path: string;
  children: ReactNode;
}

/**
 * RouteGuard applies the `ROUTE_MODULES` rule of a top-level route (issue #1670).
 * A module route delegates to `RequireModuleRoute`. An admin route renders for an
 * admin and sends an employee to the first granted web module; with none, the
 * employee gets the lock-out, never a redirect back to `/` (that would loop).
 */
export function RouteGuard({ path, children }: RouteGuardProps) {
  const { isAdmin } = useAccess();
  const access = ROUTE_MODULES[path];

  if (access === undefined) {
    // appRoutes.test.tsx maps every route, so this is unreachable in a build
    // that passed its tests.
    if (import.meta.env.DEV) {
      throw new Error(`RouteGuard: no ROUTE_MODULES entry for "${path}"`);
    }
    return <StaffNotAuthorizedPage reason="noWebAccess" />;
  }

  if (access !== 'admin') {
    return <RequireModuleRoute module={access}>{children}</RequireModuleRoute>;
  }

  if (isAdmin) {
    return <>{children}</>;
  }
  return <RedirectToFirstModule />;
}
